import "./mindmap.scss";

import { List } from "immutable";
import _, { throttle } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { PopoverBody } from "reactstrap";

import { asDropTarget } from "../../components/dragAndDrop/DroppableComponent";
import EmptyState from "../../components/emptyState";
import Icon from "../../components/interface/Icon";
import ListView from "../../components/listView/ListView";
import FocusedPopover from "../../components/popover/FocusedPopover";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { withScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import withScrollableView from "../../components/scrollable/withScrollableView";
import { MINDMAP_IMPORT_SPACING, MINDMAP_NODE_MIN_HEIGHT, MINDMAP_NODE_MIN_WIDTH } from "../../constants";
import { DragDropTypes } from "../../constants/dragDropTypes";
// import { findDOMNode } from "react-dom";
import { MINDMAP_VIEW } from "../../constants/identifiers";
import { INVERSE_ID_PREFIX } from "../../constants/idPrefixes";
import interfaceMessages from "../../constants/interfaceMessages";
import lineStyles from "../../constants/lineStyles";
import PubSubTopics from "../../constants/pubSubTopics";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import timeoutRates from "../../constants/timeoutRates";
import { mindmapOperations } from "../../modules/mindmaps";
import { immutableRecords, PropTypeShapes } from "../../types";
import { selectByPubSubTopics } from "../../types/immutableRecords/model/appState/utils";
import { disableWhenIos, wrapEditingComponent } from "../../utils/codeSplit";
import { onPasteItems } from "../../utils/copyPaste";
import { isDragMeasuringTimespan } from "../../utils/dropFunctions";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import {
  getEventIsMouse,
  getEventIsTouch,
  getPointerPositionInDOMElement,
  getScreenPointerPositionFromEvent
} from "../../utils/mouseUtils";
import rectUtils from "../../utils/rectUtils";
import uniqueId from "../../utils/uniqueId";
import RelationshipPopoverList from "../relationshipView/RelationshipPopoverList";
import { withWorkspaceContext } from "../workspace/WorkspaceContext";
import MindmapConnectionLabel from "./MindmapConnectionLabel";
import MindmapConnectorDot from "./MindmapConnectorDot";
import MindmapLine from "./MindmapLine";
import MindmapNodeView from "./MindmapNodeView";
import { connectorForRects, ConnectorLocations } from "./utils";

// import debugUtils from "../../utils/debugUtils";

const MINDMAP_DRAG_SNAP_THRESHOLD = 10;

const MINDMAP_CREATE_OFFSET_X = 20;
const MINDMAP_CREATE_OFFSET_Y = 5;

const getVirtualOffset = (xScrollPosition, yScrollPosition) => {
  return immutableRecords.PointRecord({
    x: xScrollPosition.canvas.min,
    y: yScrollPosition.canvas.min
  });
};

const findNodeForItemId = (nodes, itemId) => nodes.find(n => n.itemId === itemId);

const getTargetPointerPosition = (e, virtualOffset, domNode) => {
  const screenPointerPos = getScreenPointerPositionFromEvent(e);
  const domRect = domNode.getBoundingClientRect();
  return {
    x: screenPointerPos.x - domRect.left + virtualOffset.x,
    y: screenPointerPos.y - domRect.top + virtualOffset.y
  };
};

const getTargetSourceRect = (sourceRect, virtualOffset, domRect) => {
  const dx = virtualOffset.x - domRect.left;
  const dy = virtualOffset.y - domRect.top;
  return {
    left: sourceRect.left + dx,
    right: sourceRect.right + dx,
    top: sourceRect.top + dy,
    bottom: sourceRect.bottom + dy,
    width: sourceRect.width,
    height: sourceRect.height
  };
};

const findNodeAtPosition = (nodes, pos) =>
  nodes.findLast(n => n.rect.top <= pos.y && n.rect.bottom >= pos.y && n.rect.left <= pos.x && n.rect.right >= pos.x);

const findClosestHorizontalAlignment = (node, rect) => {
  const nodeCenter = (node.rect.left + node.rect.right) / 2;
  const left = node.rect.left - rect.left;
  const right = node.rect.right - rect.right;
  const center = nodeCenter - (rect.left + rect.right) / 2;
  const min = Math.min(Math.abs(left), Math.abs(right), Math.abs(center));
  if (min === Math.abs(center)) {
    return {
      node,
      align: ConnectorLocations.CENTER,
      distance: center,
      value: nodeCenter
    };
  } else if (min === Math.abs(left)) {
    return {
      node,
      align: ConnectorLocations.LEFT,
      distance: left,
      value: node.rect.left
    };
  }
  return {
    node,
    align: ConnectorLocations.RIGHT,
    distance: right,
    value: node.rect.right
  };
};

const findClosestVerticalAlignment = (node, rect) => {
  const nodeCenter = (node.rect.top + node.rect.bottom) / 2;
  const top = node.rect.top - rect.top;
  const bottom = node.rect.bottom - rect.bottom;
  const center = nodeCenter - (rect.top + rect.bottom) / 2;
  const min = Math.min(Math.abs(top), Math.abs(bottom), Math.abs(center));
  if (min === Math.abs(center)) {
    return {
      node,
      align: ConnectorLocations.CENTER,
      distance: center,
      value: nodeCenter
    };
  } else if (min === Math.abs(top)) {
    return {
      node,
      align: ConnectorLocations.TOP,
      distance: top,
      value: node.rect.top
    };
  }
  return {
    node,
    align: ConnectorLocations.BOTTOM,
    distance: bottom,
    value: node.rect.bottom
  };
};

const findHorizontalSnap = (nodes, rect) => {
  const aligments = nodes.map(n => findClosestHorizontalAlignment(n, rect));
  const minAlign = aligments.minBy(a => Math.abs(a.distance));
  if (minAlign && Math.abs(minAlign.distance) < MINDMAP_DRAG_SNAP_THRESHOLD) {
    return minAlign;
  }
  return null;
};

const findVerticalSnap = (nodes, rect) => {
  const aligments = nodes.map(n => findClosestVerticalAlignment(n, rect));
  const minAlign = aligments.minBy(a => Math.abs(a.distance));
  if (minAlign && Math.abs(minAlign.distance) < MINDMAP_DRAG_SNAP_THRESHOLD) {
    return minAlign;
  }
  return null;
};

const getMoveParametersForDrag = (props, dragItem, positions, domRect, allowSnap) => {
  const zoom = 1 / scalePixelsToFontSize(1, props.fontSize);

  const virtualOffset = getVirtualOffset(props.xScrollPosition, props.yScrollPosition);

  const itemIds = dragItem.multipleItemIds ? dragItem.multipleItemIds : [dragItem.itemId];
  const nodeIds = _.map(itemIds, id => {
    const n = findNodeForItemId(props.data.nodes, id);
    return n ? n.id : null;
  });

  // Work out an initial drop position
  let dropX = (virtualOffset.x + positions.targetPointerPos.x) * zoom;
  let dropY = (virtualOffset.y + positions.targetPointerPos.y) * zoom;

  let horizontalSnap = null;
  let verticalSnap = null;
  let horizontalSnapLine = null;
  let verticalSnapLine = null;
  // Now let's see if it should snap to align to an existing node
  if (allowSnap) {
    const snappableNodes = props.data.nodes.filter(n => nodeIds.indexOf(n.id) === -1);
    let sourceRect = getTargetSourceRect(positions.sourceRect, virtualOffset, domRect, zoom);
    horizontalSnap = findHorizontalSnap(snappableNodes, sourceRect);
    verticalSnap = findVerticalSnap(snappableNodes, sourceRect);

    // If we need to snap, just add the extra distance needed to snap to the correct position
    if (horizontalSnap) {
      dropX += horizontalSnap.distance * zoom;
    }
    if (verticalSnap) {
      dropY += verticalSnap.distance * zoom;
    }

    // Now calculate the dotted alignment lines we want to draw
    if (horizontalSnap) {
      horizontalSnapLine = [
        {
          x: horizontalSnap.value,
          y: Math.min(sourceRect.top, horizontalSnap.node.rect.top)
        },
        {
          x: horizontalSnap.value,
          y: Math.max(sourceRect.bottom, horizontalSnap.node.rect.bottom)
        }
      ];
    }

    if (verticalSnap) {
      verticalSnapLine = [
        {
          x: Math.min(sourceRect.left, verticalSnap.node.rect.left),
          y: verticalSnap.value
        },
        {
          x: Math.max(sourceRect.right, verticalSnap.node.rect.right),
          y: verticalSnap.value
        }
      ];
    }
  }

  const deltaX =
    dragItem.initialValues && dragItem.initialValues.mouseDownX ? dropX - dragItem.initialValues.mouseDownX : 0;
  const deltaY =
    dragItem.initialValues && dragItem.initialValues.mouseDownY ? dropY - dragItem.initialValues.mouseDownY : 0;

  return {
    sourceView: dragItem.sourceView,
    mindmapId: props.mindmapId,
    nodeIds,
    itemIds,
    dropX,
    dropY,
    deltaX,
    deltaY,
    horizontalSnapLine,
    verticalSnapLine
  };
};

const getSourceRectPointerPos = (dragItem, dropTarget) => {
  // Position the node at the top left so snapping works if it came from another mindmap view
  const sourceRectPointerPosX =
    dragItem.sourceViewType === MINDMAP_VIEW &&
    dropTarget.viewId != dragItem.sourceView &&
    dragItem.initialValues &&
    dragItem.initialValues.sourceRectPointerPosX
      ? dragItem.initialValues.sourceRectPointerPosX
      : 0;
  const sourceRectPointerPosY =
    dragItem.sourceViewType === MINDMAP_VIEW &&
    dropTarget.viewId != dragItem.sourceView &&
    dragItem.initialValues &&
    dragItem.initialValues.sourceRectPointerPosY
      ? dragItem.initialValues.sourceRectPointerPosY
      : 0;
  return { x: sourceRectPointerPosX, y: sourceRectPointerPosY };
};

const referenceOptions = (subjectId, itemId, allowedReferences, allowedInverseReferences, currentRelationships) => {
  const list1 = allowedReferences.map(ref => {
    return {
      id: ref.id,
      label: ref.label,
      icon: ref.icon,
      color: "iconMono",
      selected: currentRelationships.find(
        rel =>
          rel.reference === ref.id && (rel.subject === subjectId || (ref.bidirectional && rel.object === subjectId))
      )
    };
  });
  const list2 = allowedInverseReferences.map(ref => {
    return {
      id: INVERSE_ID_PREFIX + ref.id,
      label: ref.inverseLabel ? ref.inverseLabel : ref.label,
      icon: ref.icon,
      color: "iconMono",
      selected: currentRelationships.find(rel => rel.reference === ref.id && rel.object === subjectId)
    };
  });
  return list1.concat(list2).toArray();
};

const dropCallbacks = {
  onEnter(_props, _dragItem, _dropTarget) {
    // Do nothing
  },

  onLeave(props, _dragItem, dropTarget) {
    dropTarget.component.setSnapDragLines(null, null);

    if (props.onInternalDragLeave) {
      props.onInternalDragLeave();
    }
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    // No logical ordering, so using the selection order instead
    return props.selectedItemIds.contains(dragItem.itemId)
      ? props.selectedItemIds.filter(id => props.sortedItemIds.contains(id)).toArray()
      : [dragItem.itemId];
  },

  getDragStartParams(props, dragItem, positions, dropTarget) {
    const screenPointerPos = _.clone(positions.screenPointerPos);
    const sourceRectPointerPosX = screenPointerPos.x - positions.sourceRect.left;
    const sourceRectPointerPosY = screenPointerPos.y - positions.sourceRect.top;
    const domRect = dropTarget.component._domNode.getBoundingClientRect();
    const { dropX, dropY } = getMoveParametersForDrag(props, dragItem, positions, domRect, false);
    return {
      mouseDownX: dropX,
      mouseDownY: dropY,
      sourceRectPointerPosX,
      sourceRectPointerPosY
    };
  },

  onHover(props, dragItem, positions, dropTarget) {
    if (isDragMeasuringTimespan(dragItem)) {
      return {
        message: interfaceMessages.DRAG_DROP_MEASURE_ACROSS_VIEWS_NOT_ALLOWED,
        messageType: "error"
      };
    }

    const canDropItems = props.requestCanDropItems(dragItem, dropTarget.viewId);
    const canDrop = canDropItems.canDrop;

    if (canDrop) {
      const domRect = dropTarget.component._domNode.getBoundingClientRect();
      const result = getMoveParametersForDrag(
        props,
        dragItem,
        positions,
        domRect,
        dropTarget.viewType === dragItem.sourceViewType
      );
      dropTarget.component.setSnapDragLines(result.horizontalSnapLine, result.verticalSnapLine);

      const sourceRectPointerPos = getSourceRectPointerPos(dragItem, dropTarget);
      const sourceRectPointerPosX = sourceRectPointerPos.x;
      const sourceRectPointerPosY = sourceRectPointerPos.y;

      props.onInternalMoveEvent(
        result.sourceView,
        result.mindmapId,
        result.nodeIds,
        result.itemIds,
        result.deltaX,
        result.deltaY,
        result.dropX - sourceRectPointerPosX,
        result.dropY - sourceRectPointerPosY
      );
    }

    return {
      message: canDrop ? null : canDropItems.message,
      messageType: canDrop ? null : "error"
    };
  },

  onDrop(props, dragItem, positions, dropTarget) {
    if (isDragMeasuringTimespan(dragItem)) {
      return false;
    }
    const canDropItems = props.requestCanDropItems(dragItem, dropTarget.viewId);
    const canDrop = canDropItems.canDrop;

    dropTarget.component.setSnapDragLines(null, null);

    if (canDrop) {
      const domRect = dropTarget.component._domNode.getBoundingClientRect();
      const result = getMoveParametersForDrag(
        props,
        dragItem,
        positions,
        domRect,
        dropTarget.viewType === dragItem.sourceViewType
      );

      const sourceRectPointerPos = getSourceRectPointerPos(dragItem, dropTarget);
      const sourceRectPointerPosX = sourceRectPointerPos.x;
      const sourceRectPointerPosY = sourceRectPointerPos.y;

      props.onEventDrop(
        result.sourceView,
        result.mindmapId,
        result.nodeIds,
        result.itemIds,
        result.deltaX,
        result.deltaY,
        result.dropX - sourceRectPointerPosX,
        result.dropY - sourceRectPointerPosY
      );
    }
  },

  showCountOnPreview(_props, _dragItem, _positions, _dropTarget) {
    return true;
  },

  getDropPreviewForItem(props, dragItem, dropTarget) {
    return dropTarget.component.getDropPreviewForItem(props, dragItem, dropTarget);
  }
};

class MindmapCanvasView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      draggingConnector: false,
      connectorDragSource: null,
      connectorDragDest: null,
      connectorDragStartPos: null,
      connectorDragEndPos: null,
      horizontalSnapLine: null,
      verticalSnapLine: null,
      popoverOpen: false,
      popoverTargetId: null,
      popoverSubjectId: null,
      popoverItemId: null,
      popoverOptions: null,
      addPopoverOption: false,
      addPopoverX: null,
      addPopoverY: null
    };

    this.onScrollToItemReceived = this.onScrollToItemReceived.bind(this);
    this.onSelectItemByTopicsReceived = this.onSelectItemByTopicsReceived.bind(this);
    this.onConnectorPointerDown = this.onConnectorPointerDown.bind(this);
    this.onConnectorDragPointerMove = this.onConnectorDragPointerMove.bind(this);
    this.onConnectorDragPointerUp = this.onConnectorDragPointerUp.bind(this);
    this.showMenu = this.showMenu.bind(this);
    this.hideMenu = this.hideMenu.bind(this);
    this.menuOptionClick = this.menuOptionClick.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onCreateItem = this.onCreateItem.bind(this);
    this.hideAddNode = this.hideAddNode.bind(this);
    this.onAddNodeClick = this.onAddNodeClick.bind(this);
    this.addNodeOptionRenderer = this.addNodeOptionRenderer.bind(this);

    this._mindmapAddNodeId = "mindmapAddNodeId-" + uniqueId();

    // copy paste
    this._pubSubPasteToken = null;
    this.onPasteMessageReceived = this.onPasteMessageReceived.bind(this);
    this.onPaste = this.onPaste.bind(this);
    this.processPastingItems = this.processPastingItems.bind(this);

    // Select rectangle
    //#!if iOS !== true
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);

    this.throttledMouseMove = throttle(this.onMouseMove, timeoutRates.DRAW_SELECT_RECT_THROTTLE, {
      leading: false,
      trailing: true
    });

    this.onMouseUp = this.onMouseUp.bind(this);
    this.onClick = this.onClick.bind(this);
    this._selectStartPoint = null;
    this._visibleNodes = null;
    //#!endif
  }

  componentDidMount() {
    this.props.registerDropTarget(this, dropCallbacks);
    this._pubSubScrollToItemToken = PubSub.subscribe(PubSubTopics.scrollToItem, this.onScrollToItemReceived);
    this._pubSubSelectItemByTopicsToken = PubSub.subscribe(
      PubSubTopics.selectItemByTopics,
      this.onSelectItemByTopicsReceived
    );
    this.props.workViewRegistration.registerWorkViewCreateItemCallback(this.props.viewId, this.onCreateItem);
    this._pubSubPasteToken = PubSub.subscribe(PubSubTopics.paste, this.onPasteMessageReceived);
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("MindmapCanvasView", this.props, nextProps, true);
  // }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubScrollToItemToken);
    PubSub.unsubscribe(this._pubSubSelectItemByTopicsToken);
    this.props.workViewRegistration.unregisterWorkViewCreateItemCallback(this.props.viewId);
    this.removeConnectorDragEvents();
    PubSub.unsubscribe(this._pubSubPasteToken);
  }

  processPastingItems(itemMapList) {
    const lastSelectedIndex = this.props.data.nodes.findLastIndex(n => this.props.selectedItemIds.contains(n.itemId));
    let absX = 0;
    let absY = 0;
    if (lastSelectedIndex >= 0) {
      const lastNode = this.props.data.nodes.get(lastSelectedIndex);
      absX = lastNode.rect.left + MINDMAP_CREATE_OFFSET_X;
      absY = lastNode.rect.bottom + MINDMAP_CREATE_OFFSET_Y;
    } else {
      const viewport = this.getViewport(this.props.xScrollPosition, this.props.yScrollPosition);
      absX = (viewport.left + viewport.right) / 2 - MINDMAP_NODE_MIN_WIDTH / 2;
      absY = (viewport.top + viewport.bottom) / 2 - MINDMAP_NODE_MIN_HEIGHT / 2;
    }

    let extraActions = [];
    itemMapList.forEach(im => {
      extraActions.push(mindmapOperations.importNodes(this.props.mindmapId, [im.get("id")], absX, absY));
      // Increment the absX and absY to avoid all pasted items cover each other
      absX += MINDMAP_IMPORT_SPACING;
      absY += MINDMAP_IMPORT_SPACING;
    });

    return extraActions;
  }

  onPaste(e) {
    const {
      dispatchBatchActions,
      projectId,
      isActiveView,
      orderedTypes,
      defaultType,
      typesById,
      propertiesById,
      colorsById,
      referencesById,
      itemsById
    } = this.props;
    dispatchBatchActions(
      onPasteItems(
        e,
        projectId,
        isActiveView,
        () => true,
        this.processPastingItems,
        orderedTypes.map(t => t.id),
        defaultType,
        () => true,
        typesById,
        propertiesById,
        colorsById,
        referencesById,
        itemsById
      )
    );
  }

  onPasteMessageReceived(msg, clipboardData) {
    this.onPaste({ clipboardData });
  }

  getViewport() {
    const { xScrollPosition, yScrollPosition } = this.props;
    return immutableRecords.FlatRectRecord({
      left: xScrollPosition.viewport.min,
      right: xScrollPosition.viewport.max,
      width: xScrollPosition.viewport.range,
      top: yScrollPosition.viewport.min,
      bottom: yScrollPosition.viewport.max,
      height: yScrollPosition.viewport.range
    });
  }

  getVirtualOffsetInClass() {
    const { xScrollPosition, yScrollPosition } = this.props;
    return getVirtualOffset(xScrollPosition, yScrollPosition);
  }

  onSelectItemByTopicsReceived(msg, selectTopic) {
    const newSelectedIds = selectByPubSubTopics(
      this.props.isActiveView,
      // sort by y then x
      this.props.data.nodes
        .sort((aN, bN) => {
          if (aN.rect.top === bN.rect.top) {
            return aN.rect.left - bN.rect.left;
          } else {
            return aN.rect.top - bN.rect.top;
          }
        })
        .map(node => node.itemDisplay),
      selectTopic,
      this.props.selectedItemIds
    );
    if (newSelectedIds.length > 0) {
      this.props.onSelectItems(newSelectedIds);
      this.onScrollToItemReceived("", { id: newSelectedIds[0] });
    }
  }

  onCreateItem(typeId) {
    const lastSelectedIndex = this.props.data.nodes.findLastIndex(n => this.props.selectedItemIds.contains(n.itemId));
    if (lastSelectedIndex >= 0) {
      // If an item is selected, add it underneath that
      const lastNode = this.props.data.nodes.get(lastSelectedIndex);
      this.props.onCreateItem(
        typeId,
        this.props.mindmapId,
        lastNode.rect.left + MINDMAP_CREATE_OFFSET_X,
        lastNode.rect.bottom + MINDMAP_CREATE_OFFSET_Y
      );
    } else {
      // Otherwise, add it to the screen center
      const viewport = this.getViewport();
      const absX = (viewport.left + viewport.right) / 2 - MINDMAP_NODE_MIN_WIDTH / 2;
      const absY = (viewport.top + viewport.bottom) / 2 - MINDMAP_NODE_MIN_HEIGHT / 2;
      this.props.onCreateItem(typeId, this.props.mindmapId, absX, absY);
    }
    return true;
  }

  onDoubleClick(e) {
    const screenPointerPos = getScreenPointerPositionFromEvent(e);
    const domRect = this._domNode.getBoundingClientRect();

    //this.props.onCreateItem(this.props.defaultType, this.props.mindmapId, x, y);
    if (!hasAncestorOfClass(e.target, "mindmapNode")) {
      this.setState({
        addPopoverOpen: true,
        addPopoverX: screenPointerPos.x - domRect.left,
        addPopoverY: screenPointerPos.y - domRect.top
      });
    }
  }

  onClick(e) {
    if (this._stopClickEvent) {
      e.stopPropagation();
      this._stopClickEvent = false;
    }
    this.hideAddNode();
  }

  setSnapDragLines(horizontalSnapLine, verticalSnapLine) {
    if (horizontalSnapLine !== this.state.horizontalSnapLine || verticalSnapLine !== this.state.verticalSnapLine) {
      this.setState({
        horizontalSnapLine,
        verticalSnapLine
      });
    }
  }

  onScrollToItemReceived(msg, data) {
    this.scrollToItem(data.id);
  }

  scrollToItem(itemId) {
    const node = this.props.data.nodes.find(n => n.itemId === itemId);
    if (node) {
      this.props.xScrollActions.animateRectIntoView(node.rect);
      this.props.yScrollActions.animateRectIntoView(node.rect);
    }
  }

  addConnectorDragEvents = e => {
    // And add mouse tracking events to this main view
    if (getEventIsMouse(e)) {
      this._domNode.addEventListener("mousemove", this.onConnectorDragPointerMove, false);
      window.addEventListener("mouseup", this.onConnectorDragPointerUp, false);
    } else {
      this._domNode.addEventListener("touchmove", this.onConnectorDragPointerMove, false);
      window.addEventListener("touchend", this.onConnectorDragPointerUp, false);
    }
    // And signal to the scrollViews that a drag is in progress, so that
    // they auto-scroll when required
    PubSub.publish(PubSubTopics.dragStarted, { isTouch: true });
  };

  removeConnectorDragEvents = () => {
    this._domNode.removeEventListener("mousemove", this.onConnectorDragPointerMove, false);
    window.removeEventListener("mouseup", this.onConnectorDragPointerUp, false);
    this._domNode.removeEventListener("touchmove", this.onConnectorDragPointerMove, false);
    window.removeEventListener("touchend", this.onConnectorDragPointerUp, false);

    PubSub.publish(PubSubTopics.dragEnded, { isTouch: false });
  };

  onConnectorPointerDown(id, e) {
    const node = this.props.data.nodes.find(n => n.itemId === id);
    const virtualOffset = this.getVirtualOffsetInClass();

    const targetPointerPos = getTargetPointerPosition(e, virtualOffset, this._domNode);
    this.addConnectorDragEvents(e);
    this.setState({
      draggingConnector: true,
      connectorDragSource: node,
      connectorDragDest: null,
      connectorDragStartPos: targetPointerPos,
      connectorDragEndPos: targetPointerPos
    });
  }

  onConnectorDragPointerMove(e) {
    // Avoid scrolling when dragging the connector in iOS mode
    // Follows the similar function in Line 498 in DragAndDropContainer
    // See https://aeontimeline.atlassian.net/browse/ATR-2044
    if (getEventIsTouch(e)) {
      e.preventDefault();
    }
    const virtualOffset = this.getVirtualOffsetInClass();

    const targetPointerPos = getTargetPointerPosition(e, virtualOffset, this._domNode);

    // Find if we are on top of another node
    let connectorDragDest = findNodeAtPosition(this.props.data.nodes, targetPointerPos);
    if (connectorDragDest) {
      // If no links are allowed with that item, then bail
      const dropOptions = this.props.getConnectorDropOptions(this.state.connectorDragSource, connectorDragDest);
      if (!dropOptions.references.size && !dropOptions.inverseReferences.size) {
        connectorDragDest = null;
      }
    }

    // Now calculate line to draw - either boundary to boundary, or middle to boundary...
    let dragConnector = null;

    if (connectorDragDest) {
      dragConnector = connectorForRects(this.state.connectorDragSource.rect, connectorDragDest.rect);
    } else {
      const fakeRect = immutableRecords.FlatRectRecord({
        left: targetPointerPos.x,
        right: targetPointerPos.x,
        top: targetPointerPos.y,
        bottom: targetPointerPos.y,
        width: 0,
        height: 0
      });
      dragConnector = connectorForRects(this.state.connectorDragSource.rect, fakeRect);
    }
    const points = dragConnector.toArray();
    this.setState({
      connectorDragStartPos: points[0],
      connectorDragEndPos: points[1],
      connectorDragDest
    });
  }

  onConnectorDragPointerUp(e) {
    const virtualOffset = this.getVirtualOffsetInClass();

    const targetPointerPos = getTargetPointerPosition(e, virtualOffset, this._domNode);
    // Find if we are on top of another node
    const dropNode = findNodeAtPosition(this.props.data.nodes, targetPointerPos);

    // Get the available reference options for this drop
    let dropOptions = null;

    if (dropNode && dropNode.id !== this.state.connectorDragSource.id) {
      dropOptions = this.props.getConnectorDropOptions(this.state.connectorDragSource, dropNode);
    } else {
      dropOptions = {
        relationships: List(),
        references: List(),
        inverseReferences: List()
      };
    }

    // Remove the drawing elements of the drag...
    this.removeConnectorDragEvents();

    // If there is only one type of reference allowed, create it instantly.
    if (dropOptions.relationships.size === 0) {
      if (dropOptions.references.size === 1 && dropOptions.inverseReferences.size === 0) {
        const ref = dropOptions.references.first();
        this.props.onRelationshipChange(this.state.connectorDragSource.itemId, ref.id, dropNode.itemId, true);
        this.hideConnectorLineDrawing();
        return;
      } else if (dropOptions.references.size === 0 && dropOptions.inverseReferences.size === 1) {
        const ref = dropOptions.inverseReferences.first();
        this.props.onRelationshipChange(dropNode.itemId, ref.id, this.state.connectorDragSource.itemId, true);
        this.hideConnectorLineDrawing();
        return;
      }
    }

    if (dropOptions.references.size > 0 || dropOptions.inverseReferences.size > 0) {
      // Called with defer so that the popover cannot react to the current mouseup event
      _.defer(() =>
        this.showMenu(
          this.state.connectorDragSource.itemId,
          dropNode.itemId,
          dropOptions.references,
          dropOptions.inverseReferences,
          dropOptions.relationships,
          `mindmapNode-${dropNode.id}`
        )
      );
    } else {
      this.hideConnectorLineDrawing();
    }
  }

  hideConnectorLineDrawing() {
    // And hide the current options
    this.setState({
      draggingConnector: false,
      connectorDragSource: null,
      connectorDragDest: null,
      connectorDragStartPos: null,
      connectorDragEndPos: null
    });
  }

  showMenu(subjectId, itemId, allowedReferences, allowedInverseReferences, currentRelationships, targetElementId) {
    const popoverOptions = referenceOptions(
      subjectId,
      itemId,
      allowedReferences,
      allowedInverseReferences,
      currentRelationships
    );
    this.setState({
      popoverOpen: true,
      popoverTargetId: targetElementId,
      popoverSubjectId: subjectId,
      popoverItemId: itemId,
      popoverOptions
    });
  }

  hideMenu() {
    this.setState({
      popoverOpen: false,
      popoverTargetId: null,
      popoverSubjectId: null,
      popoverSubjectType: null,
      popoverItemId: null,
      popoverItemType: null,
      popoverOptions: null
    });
    this.hideConnectorLineDrawing();
  }

  menuOptionClick(incomingReferenceId, selected) {
    this.hideMenu();
    const isStraight = incomingReferenceId.indexOf(INVERSE_ID_PREFIX) === -1;
    const referenceId = isStraight ? incomingReferenceId : incomingReferenceId.substr(INVERSE_ID_PREFIX.length);

    if (isStraight) {
      this.props.onRelationshipChange(this.state.popoverSubjectId, referenceId, this.state.popoverItemId, selected);
    } else {
      // As it is inverse, reverse subject & object so we delete the correct relationship
      this.props.onRelationshipChange(this.state.popoverItemId, referenceId, this.state.popoverSubjectId, selected);
    }
  }

  hideAddNode() {
    this.setState({ addPopoverOpen: false });
  }

  renderMenu() {
    return <RelationshipPopoverList options={this.state.popoverOptions} onClick={this.menuOptionClick} />;
  }

  renderConnectorDragLine(virtualOffset) {
    const points = [
      {
        x: this.state.connectorDragStartPos.x - virtualOffset.x,
        y: this.state.connectorDragStartPos.y - virtualOffset.y
      },
      {
        x: this.state.connectorDragEndPos.x - virtualOffset.x,
        y: this.state.connectorDragEndPos.y - virtualOffset.y
      }
    ];
    return (
      <MindmapLine
        id={`connectorDragLine`}
        selected={true}
        points={points}
        color="iconActive"
        lineStyle={lineStyles.SOLID}
      />
    );
  }

  renderSnapDragLine(snapLine, virtualOffset) {
    let points = _.map(snapLine, p => {
      return {
        x: p.x - virtualOffset.x,
        y: p.y - virtualOffset.y
      };
    });
    return (
      <MindmapLine
        id={`snapDragLine`}
        selected={true}
        points={points}
        color="iconActive"
        lineStyle={lineStyles.THINDOT}
      />
    );
  }

  renderConnectors(connectors, selectedItemIds, virtualOffset) {
    let children = [];
    connectors.forEach(c => {
      const points = c.points
        .map(p => {
          return { x: p.x - virtualOffset.x, y: p.y - virtualOffset.y };
        })
        .toArray();

      const labelCenter =
        c.labelCenter && c.label
          ? {
            x: c.labelCenter.x - virtualOffset.x,
            y: c.labelCenter.y - virtualOffset.y
          }
          : null;

      const selected =
        selectedItemIds.findIndex(s => s === c.itemId) != -1 || selectedItemIds.findIndex(s => s === c.subjectId) != -1;

      if (!c.overlap) {
        children.push(
          <MindmapConnectorDot
            key={`${c.id}-start`}
            id={`${c.id}-start`}
            selected={selected}
            point={points[0]}
            color={c.color}
            lineStyle={c.lineStyle}
            isFiltered={c.isFiltered}
          />,
          <MindmapConnectorDot
            key={`${c.id}-end`}
            id={`${c.id}-end`}
            selected={selected}
            point={points[1]}
            color={c.color}
            lineStyle={c.lineStyle}
            isFiltered={c.isFiltered}
          />,
          <MindmapLine
            key={`${c.id}-line`}
            id={`${c.id}-line`}
            selected={selected}
            points={points}
            color={c.color}
            lineStyle={c.lineStyle}
            isFiltered={c.isFiltered}
          />
        );
        if (labelCenter) {
          children.push(
            <MindmapConnectionLabel
              key={`${c.id}-label`}
              id={`${c.id}-label`}
              selected={selected}
              point={labelCenter}
              label={c.label}
            />
          );
        }
      }
    });
    return children;
  }

  renderNodes(nodes, selectedItemIds, virtualOffset, autoEditBlankNodes, isActiveView) {
    let allowDrag = true;
    //#!if iOS === true
// allowDrag = this.props.screenContext.isEditMode;
    //#!endif

    return nodes.map(n => {
      const rect = n.rect;
      const obj = n.itemDisplay.toObject();
      const absoluteLeft = rect.left;
      const absoluteTop = rect.top;
      const relativeLeft = rect.left - virtualOffset.x;
      const relativeTop = rect.top - virtualOffset.y;
      const draggingConnector =
        this.state.draggingConnector &&
        ((this.state.connectorDragSource && n.id === this.state.connectorDragSource.id) ||
          (this.state.connectorDragDest && n.id === this.state.connectorDragDest.id));

      return (
        <MindmapNodeView
          key={n.id}
          nodeId={n.id}
          {...obj}
          isActiveView={isActiveView}
          selected={selectedItemIds.indexOf(n.itemId) >= 0}
          typeData={n.typeData}
          itemId={n.itemId}
          mindmapViewSettings={this.props.mindmapViewSettings}
          absoluteLeft={absoluteLeft}
          absoluteTop={absoluteTop}
          relativeLeft={relativeLeft}
          relativeTop={relativeTop}
          width={rect.width}
          height={rect.height}
          viewId={this.props.viewId}
          draggingConnector={draggingConnector}
          allowDrag={allowDrag}
          onClick={this.props.onItemClick}
          onDoubleClick={this.props.onItemDoubleClick}
          onConnectorPointerDown={this.onConnectorPointerDown}
          onLabelChange={this.props.onLabelChange}
          autoEditWhenBlank={autoEditBlankNodes}
          typesById={this.props.typesById}
        />
      );
    });
  }

  addNodeOptionRenderer(option, isSelected, ref) {
    const virtualOffset = this.getVirtualOffsetInClass();
    return (
      <div
        ref={ref}
        className={"relationshipPopoverListItem" + (isSelected ? " highlight" : "")}
        key={option.id}
        onClick={() => {
          this.props.onCreateItem(
            option.id,
            this.props.mindmapId,
            this.state.addPopoverX + virtualOffset.x - MINDMAP_NODE_MIN_WIDTH / 2,
            this.state.addPopoverY + virtualOffset.y - MINDMAP_NODE_MIN_HEIGHT / 2
          );
          this.hideAddNode();
        }}
      >
        {option.icon ? <Icon icon={option.icon} color={option.color ? option.color : "mono"} /> : null}
        <span>{option.label}</span>
      </div>
    );
  }

  onAddNodeClick(optIndex) {
    const virtualOffset = this.getVirtualOffsetInClass();
    this.props.onCreateItem(
      this.props.orderedTypes.find((opt, index) => index === optIndex).id,
      this.props.mindmapId,
      this.state.addPopoverX + virtualOffset.x - MINDMAP_NODE_MIN_WIDTH / 2,
      this.state.addPopoverY + virtualOffset.y - MINDMAP_NODE_MIN_HEIGHT / 2
    );
    this.hideAddNode();
  }

  getListView() {
    const { orderedTypes } = this.props;

    return (
      <ListView
        className="relationshipPopoverList"
        items={orderedTypes}
        onItemSelect={this.onAddNodeClick}
        itemRenderer={this.addNodeOptionRenderer}
        autoFocus={true}
      />
    );
  }

  renderAddPopover() {
    return this.state.addPopoverOpen ? (
      <FocusedPopover
        className="relationshipPopover"
        placement="top"
        hideArrow={false}
        isOpen={this.state.addPopoverOpen}
        target={this._mindmapAddNodeId}
        toggle={this.hideAddNode}
        trigger="legacy"
      >
        <PopoverBody onClick={e => e.stopPropagation()}>{this.getListView()}</PopoverBody>
      </FocusedPopover>
    ) : null;
  }

  getDropPreviewForItem(_props, dragItem, _dropTarget) {
    if (dragItem.itemType === DragDropTypes.TABLE_COLUMN) {
      return;
    }
    const n = this.props.requestDragPreviewDataForItem(dragItem.itemId);
    const rect = n.rect;
    let obj = n.itemDisplay.toObject();
    const absoluteLeft = rect.left;
    const absoluteTop = rect.top;
    const virtualOffset = this.getVirtualOffsetInClass();
    const relativeLeft = rect.left - virtualOffset.x;
    const relativeTop = rect.top - virtualOffset.y;
    const draggingConnector = null;

    return (
      <MindmapNodeView
        key={n.id}
        nodeId={n.id}
        {...obj}
        typeData={n.typeData}
        itemId={n.itemId}
        mindmapViewSettings={this.props.mindmapViewSettings}
        absoluteLeft={absoluteLeft}
        absoluteTop={absoluteTop}
        relativeLeft={relativeLeft}
        relativeTop={relativeTop}
        width={rect.width}
        height={rect.height}
        viewId={this.props.viewId}
        draggingConnector={draggingConnector}
        allowDrag={false}
        onClick={() => {}}
        onSuperClick={() => {}}
        onConnectorPointerDown={() => {}}
        onLabelChange={() => {}}
        autoEditWhenBlank={false}
        typesById={this.props.typesById}
      />
    );
  }

  getVisibleNodes() {
    const viewport = this.getViewport();
    const visibleNodes = this.props.data.nodes.filter(n => {
      return rectUtils.rectOverlapsRect(n.rect, viewport);
    });
    return visibleNodes;
  }

  //#!if iOS !== true
  onMouseDown(e) {
    // Should only response to mouse left button click
    if (!e || (e.type === "mousedown" && e.button !== 0)) {
      return;
    }
    if (!hasAncestorOfClass(e.target, "mindmapNode")) {
      this._visibleNodes = this.getVisibleNodes();
      // Add event listeners
      document.addEventListener("mousemove", this.throttledMouseMove);
      document.addEventListener("mouseup", this.onMouseUp);
      document.addEventListener("touchmove", this.throttledMouseMove);
      document.addEventListener("touchend", this.onMouseUp);
      // Log the start point
      this._selectStartPoint = getPointerPositionInDOMElement(e, this._domNode);
    }
  }

  updateSelectRectangleDom(rect) {
    const { width, height, top, left } = rect;
    this._selectRectangleDom.style.width = `${width}px`;
    this._selectRectangleDom.style.height = `${height}px`;
    this._selectRectangleDom.style.top = `${top}px`;
    this._selectRectangleDom.style.left = `${left}px`;
  }

  calcSelectRectangle(startPoint, endPoint) {
    if (startPoint && endPoint) {
      const { x: sX, y: sY } = startPoint;
      const { x: eX, y: eY } = endPoint;
      const width = Math.abs(sX - eX);
      const height = Math.abs(sY - eY);
      const left = Math.min(sX, eX);
      const right = Math.max(sX, eX);
      const top = Math.min(sY, eY);
      const bottom = Math.max(sY, eY);
      return { width, height, left, right, top, bottom };
    }
    return null;
  }

  onMouseMove(e) {
    const endPoint = getPointerPositionInDOMElement(e, this._domNode);
    this._selectEndPoint = endPoint;
    // Update end point and the select rectangle
    const selectRect = this.calcSelectRectangle(this._selectStartPoint, endPoint);
    if (selectRect) {
      this.updateSelectRectangleDom(selectRect);
      this.selectCoveredNodes(selectRect);
    }
  }

  selectCoveredNodes(selectRect) {
    let selectedIds = [];
    this._visibleNodes.forEach(n => {
      const { x, y } = this.getVirtualOffsetInClass();
      const adjustNodeRect = n.rect.withMutations(rm => {
        rm.update("left", l => l - x);
        rm.update("right", r => r - x);
        rm.update("top", t => t - y);
        rm.update("bottom", b => b - y);
      });
      if (rectUtils.rectOverlapsRect(adjustNodeRect, selectRect)) {
        selectedIds.push(n.itemId);
      }
    });
    this.props.onSelectItems(selectedIds);
  }

  onMouseUp(_e) {
    // If has moved, should stop the click event
    // the parent click event may unselect all items
    if (this._selectStartPoint && this._selectEndPoint) {
      const { width, height } = this.calcSelectRectangle(this._selectStartPoint, this._selectEndPoint);
      if (width > 0 || height > 0) {
        this._stopClickEvent = true;
      }
    }

    // Remove event listeners
    document.removeEventListener("mousemove", this.throttledMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);
    document.removeEventListener("touchmove", this.throttledMouseMove);
    document.removeEventListener("touchend", this.onMouseUp);
    // Remove select rectangle
    this.updateSelectRectangleDom({ width: 0, height: 0, top: 0, left: 0 });
    // Clear the select rectangle parameters
    this._selectStartPoint = null;
    this._selectEndPoint = null;
  }
  //#!endif

  render() {
    const data = this.props.data;

    const viewport = this.getViewport();

    const virtualOffset = this.getVirtualOffsetInClass();

    const width = viewport.width;
    const height = viewport.height;

    const canvasStyle = {
      width: `${width}px`,
      height: `${height}px`
    };

    let emptyState = null;
    if (this.props.data.nodes.count() === 0) {
      //#!if readonly === true
      emptyState = (
        <EmptyState type="text" absolute={true}>
          <div key="content">
            <p>No items to display.</p>
          </div>
        </EmptyState>
      );
      //#!else
// emptyState = <EmptyState view={MINDMAP_VIEW} type="view" absolute={true} />;
      //#!endif
    }

    return (
      <div
        className="mindmapViewCanvas"
        style={canvasStyle}
        ref={node => (this._domNode = node)}
        onDoubleClick={disableWhenIos(this.onDoubleClick)}
        onClick={this.onClick}
        onPaste={this.onPaste}
        onMouseDown={this.onMouseDown}
      >
        {/* #!if iOS !== true */}
        <div className="selectRectangle" ref={node => (this._selectRectangleDom = node)} />
        {/* #!endif */}
        {this.renderNodes(
          data.nodes,
          this.props.selectedItemIds,
          virtualOffset,
          this.props.autoEditBlankNodes,
          this.props.isActiveView
        )}
        {this.renderConnectors(data.connectors, this.props.selectedItemIds, virtualOffset)}
        {this.state.draggingConnector ? this.renderConnectorDragLine(virtualOffset) : null}
        {this.state.horizontalSnapLine ? this.renderSnapDragLine(this.state.horizontalSnapLine, virtualOffset) : null}
        {this.state.verticalSnapLine ? this.renderSnapDragLine(this.state.verticalSnapLine, virtualOffset) : null}
        {emptyState}
        {wrapEditingComponent(
          <Fragment>
            {this.state.popoverOpen ? <div className="relationshipPopoverBackdrop" onClick={this.hideMenu} /> : null}
            {this.state.popoverOpen ? (
              <FocusedPopover
                className="relationshipPopover"
                placement="bottom"
                hideArrow={false}
                isOpen={this.state.popoverOpen}
                target={this.state.popoverTargetId}
                toggle={this.hideMenu}
                trigger="legacy"
              >
                <PopoverBody onClick={e => e.stopPropagation()}>{this.renderMenu()}</PopoverBody>
              </FocusedPopover>
            ) : null}
            <div
              id={this._mindmapAddNodeId}
              style={{
                position: "absolute",
                top: this.state.addPopoverX ? `${this.state.addPopoverY}px` : 0,
                left: this.state.addPopoverY ? `${this.state.addPopoverX}px` : 0
              }}
            />
            {this.renderAddPopover()}
          </Fragment>
        )}
      </div>
    );
  }
}

MindmapCanvasView.propTypes = {
  fontSize: PropTypes.number.isRequired,
  viewId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  mindmapId: PropTypes.string.isRequired,
  defaultType: PropTypes.string.isRequired,
  data: PropTypeShapes.mindmap.isRequired,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onInternalMoveEvent: PropTypes.func.isRequired,
  onInternalDragLeave: PropTypes.func.isRequired,
  onEventDrop: PropTypes.func.isRequired,
  onCreateItem: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func,
  getConnectorDropOptions: PropTypes.func.isRequired,
  onRelationshipChange: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onItemDoubleClick: PropTypes.func.isRequired,
  onSelectItems: PropTypes.func.isRequired,
  mindmapViewSettings: PropTypeShapes.mindmapViewSettings.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,
  requestDragPreviewDataForItem: PropTypes.func.isRequired,
  orderedTypes: ImmutablePropTypes.list,
  requestCanDropItems: PropTypes.func.isRequired,

  // copy paste
  dispatchBatchActions: PropTypes.func,
  typesById: ImmutablePropTypes.map,
  propertiesById: ImmutablePropTypes.map,
  colorsById: ImmutablePropTypes.map,
  referencesById: ImmutablePropTypes.map,
  itemsById: ImmutablePropTypes.map,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired,

  // Also accessed by ScrollableView
  requestViewBoundsForZoom: PropTypes.func.isRequired,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired,
  yScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  yScrollActions: PropTypeShapes.scrollActions.isRequired,

  // Workspace context
  workViewRegistration: PropTypes.object.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object,
  //#!endif

  // Starting scroll positions
  initialScrollLeft: PropTypes.number,
  initialScrollTop: PropTypes.number
};

const scrollableViewOptions = {
  scrollHorizontally: true,
  scrollVertically: true,
  showHorizontalScrollbar: true,
  showVerticalScrollbar: true
};

const normalView = withWorkspaceContext(
  withScrollableView(withScrollPositionContext(asDropTarget(MindmapCanvasView, MINDMAP_VIEW)), scrollableViewOptions)
);

let exportedView = normalView;
//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default exportedView;
